import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { is404Error } from '~/shared/error-code/error-code';
import { HTTPError } from '~/shared/http-fetcher/http-error';

import type { ChangeProposalCompareResponse } from '../../data-contract/chat';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export const useChangeProposalCompare = ({
    blockId,
    enabled = true,
}: {
    readonly blockId: string;
    readonly enabled?: boolean;
}): UseQueryResult<ChangeProposalCompareResponse, Error> => {
    const { fetchChangeProposalCompare } = useApplicationFetchers();

    return useQuery({
        enabled: enabled === true,
        queryFn: async () => {
            const result = await fetchChangeProposalCompare(blockId);

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: jobArchitectureKeys.changeProposalCompare(blockId),
        // The payload is frozen server-side; expiry surfaces as a 404, which no
        // retry can fix. Transient failures keep the bounded default retries.
        retry: (failureCount, error) =>
            !is404Error(error) && !(error instanceof HTTPError && error.response.status === 404) && failureCount < 3,
        staleTime: Number.POSITIVE_INFINITY,
    });
};

/** Drops the cached compare snapshot of a block, e.g. once the backend may have rewritten it. */
export const useInvalidateChangeProposalCompare = (): ((blockId: string) => void) => {
    const queryClient = useQueryClient();

    return useCallback(
        (blockId: string) => {
            void queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.changeProposalCompare(blockId) });
        },
        [queryClient],
    );
};
